import { sessionFreshen } from '@/api/session/sessionFreshen'
import { Button } from '@/components/action/Button'
import { toastError } from '@/components/action/Toaster'
import { BasicPage } from '@/components/layout/BasicPage'
import { CheckBox } from '@/components/scalar/CheckBox'
import { TextBox } from '@/components/scalar/TextBox'
import { useApi } from '@/hooks/useApi'
import { useTenantId } from '@/hooks/useTenantId'
import { resolveLoginRedirectTarget } from '@/utilities/authRedirect'
import { routes } from '@/utilities/routes'
import { sessionEmailAuthSpec, sessionEmailAuthVerifySpec, sessionGoogleAuthSpec } from 'aevocam-rest-api-spec'
import { useMemo, useRef, useState, type SubmitEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export function LogInPage() {

  const tenantId = useTenantId()
  const api = useApi()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [authenticationCode, setAuthenticationCode] = useState('')
  const [googleBusy, setGoogleBusy] = useState(false)
  const [newUser, setNewUser] = useState(false)

  const authenticationCodeRef = useRef<HTMLInputElement | null>(null)

  //--------------------------------------------------------------------------------
  let cleanEmail = useMemo(() => {
    let newValue = email.trim()
    if (!newValue.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) return undefined
    return newValue
  }, [email])

  //--------------------------------------------------------------------------------
  let cleanAuthCode = useMemo(() => {
    if (authenticationCode.match(/[A-Z]/i)) return undefined
    const newValue = authenticationCode.replace(/[^0-9]/g, '')
    if (newValue.length !== 6) return undefined
    return newValue
  }, [authenticationCode])

  //--------------------------------------------------------------------------------
  async function handleSend(ev: SubmitEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (!cleanEmail) return
    setSending(true)
    setEmailSent(false)
    try {

      const res = await api.request(sessionEmailAuthSpec, {
        path: {
          tenant: tenantId ?? '',
        },
        body: {
          email,
          createIfNotFound: newUser,
        },
      })

      if (res.status !== 200) {
        console.error(res)
        alert('Error: ' + res.error)
        return
      }
      setEmailSent(true)
      setTimeout(() => {
        authenticationCodeRef.current?.focus()
      }, 100)
    } finally {
      setSending(false)
    }
  }

  //--------------------------------------------------------------------------------
  async function handleVerify(ev: SubmitEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (!cleanEmail || !cleanAuthCode) return
    const res = await api.request(sessionEmailAuthVerifySpec, {
      path: {
        tenant: tenantId ?? '',
      },
      body: {
        email: cleanEmail,
        code: cleanAuthCode,
      },
    })
    if (res.status != 200) {
      console.error(res)
      alert('Error: ' + res.error)
      return
    }
    await sessionFreshen(api, tenantId)
    const redirectTo = resolveLoginRedirectTarget(location.search) ?? routes.home(tenantId)
    navigate(redirectTo, { replace: true })
  }

  //--------------------------------------------------------------------------------
  async function handleGoogleLogin() {
    setGoogleBusy(true)
    try {
      const res = await api.request(sessionGoogleAuthSpec, {
        path: { tenant: tenantId ?? '' },
      })
      if (res.status === 200 && res.data?.url) {
        window.location.href = res.data.url
        return
      }
      console.error(res)
      toastError(res.error ?? 'Could not start Google login')
    } finally {
      setGoogleBusy(false)
    }
  }

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title='Log in'
    >

      <div className='FlexBricks'>

        <div className='Cutout' style={{ maxWidth: '35rem'}}>

          <form onSubmit={handleSend} className='Wrapped'>

            <Button
              type='button'
              label='Log in via Google'
              icon='google'
              busy={googleBusy}
              disabled={googleBusy}
              onClick={handleGoogleLogin}
            />

            <br /><br />
            Or
            <br /><br />

            <h2>Log in via email</h2>

            <p>
              Check &quot;New user&quot; if you are new to Aevocam.
            </p>

            <TextBox
              label='Email address'
              type='email'
              placeholder='jdoe@example.com'
              width='25rem'
              value={email}
              setValue={setEmail}
            />
            <CheckBox
              label='New user'
              tooltip='I am a new user and want to create an account'
              value={newUser}
              setValue={setNewUser}
            />
            <Button
              type='submit'
              label='Send authentication email'
              icon='envelope'
              busy={sending}
              disabled={!cleanEmail || sending}
            />
          </form>

        </div>

        {emailSent && (<>
          <form onSubmit={handleVerify}>
            <div className='Cutout Wrapped'>
              <TextBox
                ref={authenticationCodeRef}
                label='Authentication code'
                type='text'
                placeholder='000-000'
                value={authenticationCode}
                setValue={setAuthenticationCode}
              />
              <Button
                type='submit'
                label='Verify authentication code'
                icon='unlock'
                disabled={!cleanAuthCode}
              />
            </div>
          </form>
        </>)}

      </div>

    </BasicPage>
  )
}
